import {
  ChartHeater,
  ColoredContainer,
  CustomChangeWrapper,
  CustomTextHeader,
  HeatWrapper,
  HeaterStatistic,
  StyledDetailsText,
  StyledHeaderWrapper,
  StyledSelect,
  TimeIntervals,
  Wrapper,
  WrapperInterval,
} from './styled'
import { ChartIntervalSet } from '../config'
import { PolarityNumber } from 'components'
import React, { useEffect, useState } from 'react'
import { useWindowSize } from 'hooks'
import Big from 'big.js'
import { GLOBAL_COLORS } from 'theme'
import { ProductChart } from './chart'
import { byXAxis, getTimeWindow } from './services'
import { buildTimeSeriesFromPriceHistory } from 'components/charts/atomic-product-chart/services'
import { ProductPageComponentProps } from '../../../pages/index-details/interfaces'
import { assertMultiply } from 'utils'
import { ChartIntervalTypes } from 'pages/index-details/storage/interface'
import { ChartPlot } from 'pages/portfolio/storage/interface'
import moment from 'moment'

export const ChartComponent: React.FC<Pick<ProductPageComponentProps, 'store'>> = ({ store }) => {
  const { dailyStats, product, dispatch, timeWindow } = store
  const startWindow = moment().unix() - timeWindow.range
  const [data, setData] = useState<ChartPlot[]>(
    dailyStats.priceHistory
      .filter((el) => moment(el.date).unix() > startWindow)
      .map(buildTimeSeriesFromPriceHistory),
  )

  useEffect(() => {
    setData(
      dailyStats.priceHistory
        .filter((el) => moment(el.date).unix() > startWindow)
        .map(buildTimeSeriesFromPriceHistory),
    )
  }, [store])

  const { symbol } = product
  const basePrice = data.length > 0 ? Big(data[data.length - 1].y) : Big(0)
  const { isMobile } = useWindowSize()

  const [dataIndex, setDataIndex] = useState(0)
  const [isMouseOut, setMouseOut] = useState(true)

  const firstPoint = data[0]
  const sortedChartData = data.sort(byXAxis)
  const isPortfolio = false
  const firstItem = firstPoint?.y ?? '0'

  const currentChange: Big = Big(firstItem).gt(0) ? basePrice.div(firstItem).sub(1) : Big(0)
  const hoverPrice: Big = data[dataIndex] ? Big(data[dataIndex].y) : Big(0)
  const hoverPriceChange: Big =
    hoverPrice.gt(0) && Big(firstItem).gt(0) ? hoverPrice.div(firstItem).sub(1) : Big(-1)

  assertMultiply(currentChange, 100)
  assertMultiply(hoverPriceChange, 100)

  const processedPriceValue = isMouseOut
    ? currentChange.mul(100).round(2, 2)
    : hoverPriceChange.mul(100).round(2, 2)

  return (
    <Wrapper>
      <StyledHeaderWrapper>
        <ChartHeater plateFormate={!!symbol} isPortfolio={isPortfolio ?? false}>
          <HeatWrapper isPortfolio={isPortfolio}>
            <HeaterStatistic fullWidth={isPortfolio}>
              <CustomTextHeader type={'small'}>Price</CustomTextHeader>
              <CustomChangeWrapper>
                <ColoredContainer color={GLOBAL_COLORS.coralBlue} />
                <StyledDetailsText type={'p'}>
                  {`$${
                    isMouseOut
                      ? basePrice.round(2, 2).toFixedSpecial(2)
                      : hoverPrice.round(2, 2).toFixedSpecial(2)
                  } `}
                </StyledDetailsText>
                <PolarityNumber
                  processedValue={+processedPriceValue}
                  textType={'p'}
                  type={'percent'}
                  suffix={'%)'}
                  prefix={
                    processedPriceValue.gt(0) ? '(+ ' : processedPriceValue.lt(0) ? '(- ' : '('
                  }
                />
              </CustomChangeWrapper>
            </HeaterStatistic>
          </HeatWrapper>
        </ChartHeater>
        {!isMobile ? (
          <WrapperInterval>
            {ChartIntervalSet.map((e) => (
              <TimeIntervals
                isPortfolio={isPortfolio}
                key={e.name}
                active={timeWindow.name === e.name}
                onClick={() => {
                  dispatch({ type: 'set_chart_time_window', payload: getTimeWindow(e.name) })
                }}
              >
                {e.name}
              </TimeIntervals>
            ))}
          </WrapperInterval>
        ) : (
          <StyledSelect
            list={ChartIntervalSet}
            onChange={(value) =>
              dispatch({
                type: 'set_chart_time_window',
                payload: getTimeWindow(value as ChartIntervalTypes),
              })
            }
            borderRadiusStyle={'right'}
          />
        )}
      </StyledHeaderWrapper>

      <ProductChart
        chartData={sortedChartData}
        indexCallback={setDataIndex}
        mouseOut={setMouseOut}
        padding={10}
        productSymbol={symbol}
        isPortfolio={isPortfolio}
      />
    </Wrapper>
  )
}
